import { Brain, Sparkles, CircuitBoard, Check, X } from "lucide-react";

const MODEL_META = {
  claude: { label: "Claude Sonnet 4.5", short: "CLAUDE", Icon: Brain, accent: "text-orange-300" },
  gpt: { label: "GPT-5.2", short: "GPT", Icon: Sparkles, accent: "text-emerald-300" },
  gemini: { label: "Gemini 3 Pro", short: "GEMINI", Icon: CircuitBoard, accent: "text-sky-300" },
};

export default function ConsensusCard({ models = [], consensus = [] }) {
  const topPick = consensus[0];

  return (
    <div
      className="glass border border-white/15 p-6 sm:p-8 relative overflow-hidden maze-texture"
      data-testid="consensus-card"
    >
      <div className="absolute inset-0 maze-texture opacity-30 pointer-events-none" />
      <div className="relative">
        <div className="flex items-center justify-between mb-6">
          <div className="flex flex-col">
            <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-400 uppercase">
              AI Consensus Engine
            </span>
            <h2 className="font-display text-3xl font-black tracking-tighter text-white mt-1">
              Ensemble Verdict
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {models.map((m) => {
              const meta = MODEL_META[m.id] || { short: m.id?.toUpperCase(), Icon: Brain, accent: "text-white" };
              const Icon = meta.Icon;
              return (
                <div
                  key={m.id}
                  className={`flex items-center gap-1.5 px-2 py-1 border ${
                    m.ok ? "border-white/20 bg-white/[0.04]" : "border-red-500/30 bg-red-500/[0.06]"
                  }`}
                  data-testid={`model-status-${m.id}`}
                  title={m.ok ? `${meta.label} responded` : `${meta.label} failed`}
                >
                  <Icon className={`w-3 h-3 ${meta.accent}`} strokeWidth={2} />
                  <span className="font-mono text-[10px] tracking-wider text-white">
                    {meta.short}
                  </span>
                  {m.ok ? (
                    <Check className="w-3 h-3 text-green-400" strokeWidth={2.5} />
                  ) : (
                    <X className="w-3 h-3 text-red-400" strokeWidth={2.5} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Model summaries grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {models.map((m) => {
            const meta = MODEL_META[m.id] || { label: m.id, Icon: Brain, accent: "text-white" };
            const Icon = meta.Icon;
            return (
              <div
                key={m.id}
                className="border border-white/10 bg-black/30 p-4 flex flex-col gap-2"
                data-testid={`model-summary-${m.id}`}
              >
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${meta.accent}`} strokeWidth={2} />
                  <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-400 uppercase">
                    {meta.label}
                  </span>
                </div>
                <p className="text-sm text-zinc-200 leading-relaxed min-h-[3.5em]">
                  {m.ok && m.verdict_summary
                    ? m.verdict_summary
                    : m.ok
                    ? `Returned ${m.picks?.length || 0} picks.`
                    : <span className="text-red-400/80 font-mono text-xs">{m.raw || "No response"}</span>}
                </p>
                {m.ok && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(m.picks || []).map((p, idx) => (
                      <span
                        key={`${m.id}-${idx}`}
                        className={`font-mono text-[10px] tracking-wider px-1.5 py-0.5 border ${
                          String(p.direction).toUpperCase() === "LONG"
                            ? "border-green-500/30 text-green-400"
                            : "border-red-500/30 text-red-400"
                        }`}
                      >
                        {p.symbol} · {String(p.direction).toUpperCase()} · {p.conviction}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Hero consensus pick */}
        {topPick && (
          <div className="border border-white/20 bg-black/40 p-5" data-testid="hero-consensus-pick">
            <div className="flex items-center justify-between mb-3">
              <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-400 uppercase">
                Top Consensus Pick · {topPick.voter_count}/3 models agree
              </span>
              <span
                className={`font-mono text-[10px] tracking-wider px-2 py-0.5 border ${
                  topPick.direction === "LONG"
                    ? "border-green-500/40 text-green-400 bg-green-500/5"
                    : "border-red-500/40 text-red-400 bg-red-500/5"
                }`}
              >
                {topPick.direction}
              </span>
            </div>
            <div className="flex flex-wrap items-end gap-6">
              <div>
                <span className="font-mono text-xs text-zinc-500">SYMBOL</span>
                <div className="font-display text-5xl font-black tracking-tighter text-white">
                  {topPick.symbol}
                </div>
              </div>
              <div>
                <span className="font-mono text-xs text-zinc-500">CONVICTION</span>
                <div className="font-mono text-3xl font-bold text-white tabular-nums">
                  {topPick.avg_conviction.toFixed(1)}<span className="text-zinc-600 text-lg">/10</span>
                </div>
              </div>
              <div>
                <span className="font-mono text-xs text-zinc-500">ENTRY</span>
                <div className="font-mono text-xl font-medium text-white tabular-nums">
                  ${topPick.avg_entry?.toFixed(2)}
                </div>
              </div>
              <div>
                <span className="font-mono text-xs text-zinc-500">TARGET</span>
                <div className="font-mono text-xl font-medium text-green-400 tabular-nums">
                  ${topPick.avg_target?.toFixed(2)}
                </div>
              </div>
              <div>
                <span className="font-mono text-xs text-zinc-500">STOP</span>
                <div className="font-mono text-xl font-medium text-red-400 tabular-nums">
                  ${topPick.avg_stop?.toFixed(2)}
                </div>
              </div>
              <div>
                <span className="font-mono text-xs text-zinc-500">HORIZON</span>
                <div className="font-mono text-base text-white">{topPick.horizon}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
